import * as React from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { Translate } from '../../../shared/LanguageService';
import { ICloneOrderCopyDetails, ISelectOption } from '../OrdersApi';
import { APARTMENT_OBJECT_TYPE } from './useCloneOrderModal';

// Matches [OrderCollection].[Name] — nvarchar(255) — and the backend validator.
export const ORDER_COLLECTION_NAME_MAX_LENGTH = 255;

interface IProps {
    value: ICloneOrderCopyDetails;
    onChange: (patch: Partial<ICloneOrderCopyDetails>) => void;
    objectTypeOptions: ISelectOption[];
    isCommercial: boolean;
    disabled: boolean;
}

export const CloneOrderDetailsForm = (props: IProps) => {
    const { value, onChange, objectTypeOptions, isCommercial, disabled } = props;
    const isApartment = !isCommercial && value.objectType === APARTMENT_OBJECT_TYPE;

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            <TextField
                label={Translate('view-order/clone/modal/details/collection-name')}
                value={value.orderCollectionName}
                onChange={(e) => onChange({ orderCollectionName: e.target.value })}
                required
                error={value.orderCollectionName.trim().length === 0}
                fullWidth
                disabled={disabled}
                inputProps={{ maxLength: ORDER_COLLECTION_NAME_MAX_LENGTH }}
            />

            <TextField
                label={Translate('view-order/clone/modal/details/notes')}
                value={value.notes}
                onChange={(e) => onChange({ notes: e.target.value })}
                multiline
                minRows={2}
                fullWidth
                disabled={disabled}
            />

            <TextField
                select
                label={Translate('view-order/clone/modal/details/object-type')}
                value={value.objectType ? String(value.objectType) : ''}
                onChange={(e) => onChange({ objectType: Number(e.target.value) })}
                fullWidth
                disabled={disabled}
            >
                {objectTypeOptions.map((option) => (
                    <MenuItem key={option.id} value={String(option.id)}>
                        {Translate(option.name)}
                    </MenuItem>
                ))}
            </TextField>

            <Box display="flex" gap={2}>
                <TextField
                    type="number"
                    label={Translate('view-order/clone/modal/details/area')}
                    value={value.objectAreaSquareMeters}
                    onChange={(e) => onChange({ objectAreaSquareMeters: e.target.value })}
                    fullWidth
                    disabled={disabled}
                    inputProps={{ min: 0 }}
                />
                <TextField
                    type="number"
                    label={Translate('view-order/clone/modal/details/rooms')}
                    value={value.objectNumberOfRooms}
                    onChange={(e) => onChange({ objectNumberOfRooms: e.target.value })}
                    fullWidth
                    disabled={disabled}
                    inputProps={{ min: 0 }}
                />
                <TextField
                    type="number"
                    label={Translate('view-order/clone/modal/details/year')}
                    value={value.objectYearOfConstruction}
                    onChange={(e) => onChange({ objectYearOfConstruction: e.target.value })}
                    fullWidth
                    disabled={disabled}
                />
            </Box>

            {isApartment && (
                <Box display="flex" flexDirection="column" gap={2}>
                    <Box display="flex" gap={2}>
                        <TextField
                            label={Translate('view-order/clone/modal/details/apartment-floor')}
                            value={value.apartmentFloor}
                            onChange={(e) => onChange({ apartmentFloor: e.target.value })}
                            fullWidth
                            disabled={disabled}
                        />
                        <TextField
                            label={Translate('view-order/clone/modal/details/apartment-access-code')}
                            value={value.apartmentAccessCode}
                            onChange={(e) => onChange({ apartmentAccessCode: e.target.value })}
                            fullWidth
                            disabled={disabled}
                        />
                    </Box>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={value.apartmentHasBalcony === true}
                                onChange={(e) => onChange({ apartmentHasBalcony: e.target.checked })}
                                disabled={disabled}
                            />
                        }
                        label={Translate('view-order/clone/modal/details/apartment-has-balcony')}
                    />
                </Box>
            )}
        </Box>
    );
};
